import { useMemo, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import ListSubheader from '@mui/material/ListSubheader';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

import { ExpensesCalculator } from '../calculator/Calculator';
import Chart from '../chart/Chart';
import { CarReliabilityCard } from './CarReliabilityCard';
import { GenerationGuide } from './GenerationGuide';
import { GenerationPicker, type GenerationFilter } from './GenerationPicker';
import { LiveListings } from './LiveListings';
import { PageContainer } from '../layout/PageContainer';
import { bgnToEur } from '../../domain/currency';
import { getCarImages } from '../../domain/carImages';
import type { EngineSpec } from '../../domain/expenses';
import { generationYearRange } from '../../domain/listings';
import {
  computeAveragePrice,
  computePriceTrend,
  formatGenerationName,
  getAvailableCountries,
  getGenerationVariationKeys,
  getYearPriceStats,
  mostCommonVariationKey,
  recentSnapshots,
  summarizeGenerations,
  type PriceTrendDirection,
} from '../../domain/pricing';
import { useEngineSpecs } from '../../hooks/useEngineSpecs';
import { useHistoricalPrices } from '../../hooks/useHistoricalPrices';
import { LISTINGS_API_URL } from '../../hooks/useLiveListings';

const TREND_STYLE: Record<PriceTrendDirection, { icon: typeof TrendingUpIcon; color: 'error.main' | 'success.main' | 'text.secondary' }> = {
  up: { icon: TrendingUpIcon, color: 'error.main' },
  down: { icon: TrendingDownIcon, color: 'success.main' },
  flat: { icon: TrendingFlatIcon, color: 'text.secondary' },
};

interface StatCardProps {
  label: string;
  value: string;
  caption?: string;
  icon?: ReactNode;
  valueColor?: string;
}

function StatCard({ label, value, caption, icon, valueColor }: StatCardProps) {
  return (
    <Card sx={{ p: { xs: 2, sm: 2.5 }, minWidth: 0 }}>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {label}
      </Typography>
      <Stack direction="row" spacing={1} alignItems="center">
        {icon}
        <Typography variant="h5" sx={{ color: valueColor, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          {value}
        </Typography>
      </Stack>
      {caption && (
        <Typography variant="caption" color="text.secondary">
          {caption}
        </Typography>
      )}
    </Card>
  );
}

/** Scraped spec text with its " year" / "HPW" suffixes trimmed, or a dash when the field is empty. */
function specText(text: string | undefined): string {
  const cleaned = (text ?? '').replace(/\s*year$/i, '').replace(/\s*HPW$/i, ' hp').trim();
  return cleaned || '—';
}

const SPEC_FIELDS: Array<{ label: string; get: (spec: EngineSpec) => string }> = [
  {
    label: 'Production years',
    get: (spec) => `${specText(spec.productionStartYear)} - ${spec.productionEndYear ? specText(spec.productionEndYear) : 'now'}`,
  },
  { label: 'Power', get: (spec) => specText(spec.power) },
  { label: 'Fuel', get: (spec) => [spec.fuelType, spec.fuelSystem].filter(Boolean).join(', ') || '—' },
  { label: 'Weight', get: (spec) => specText(spec.weight) },
  { label: 'Dimensions (L/W/H)', get: (spec) => [spec.length, spec.width, spec.height].map(specText).join(' / ') },
  { label: 'Combined consumption', get: (spec) => specText(spec.combinedConsumption) },
  { label: 'Urban consumption', get: (spec) => specText(spec.urbanConsumption) },
  { label: 'Emission standard', get: (spec) => specText(spec.emissionStandard) },
];

/** Buyers care about where prices are heading now, not since tracking began years ago. */
const TREND_WINDOW_DAYS = 90;

function formatEur(bgn: number): string {
  return `${Math.round(bgnToEur(bgn)).toLocaleString()} €`;
}

export function CarDetailsPage() {
  const { make = '', model = '' } = useParams<{ make: string; model: string }>();
  const [activeGenerationKey, setActiveGenerationKey] = useState<string | null>(null);
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [selectedGenerations, setSelectedGenerations] = useState<string[]>([]);

  const { data: snapshots = [], isLoading, isError } = useHistoricalPrices(make, model);
  const latestSnapshot = snapshots[snapshots.length - 1];
  const availableCountries = useMemo(() => getAvailableCountries(snapshots), [snapshots]);

  function toggleCountry(country: string) {
    setSelectedCountries((prev) =>
      prev.includes(country) ? prev.filter((c) => c !== country) : [...prev, country],
    );
  }

  const generationVariationKeys = useMemo(
    () => (latestSnapshot ? getGenerationVariationKeys(latestSnapshot) : []),
    [latestSnapshot],
  );
  const generations = useMemo(() => (latestSnapshot ? summarizeGenerations(latestSnapshot) : []), [latestSnapshot]);

  const { specsByKey } = useEngineSpecs(make, model, generationVariationKeys);
  // Only a stand-in thumbnail for ads without a photo of their own.
  const fallbackImageUrl = useMemo(() => getCarImages(make, model)[0]?.thumbUrl ?? null, [make, model]);

  const activeEngine = activeGenerationKey ? specsByKey.get(activeGenerationKey) ?? null : null;
  // With no engine picked, the running costs and specs show the most advertised engine rather than nothing.
  const defaultEngineKey = useMemo(
    () => (latestSnapshot ? mostCommonVariationKey(latestSnapshot, selectedGenerations) : null),
    [latestSnapshot, selectedGenerations],
  );
  const costEngineKey = activeGenerationKey ?? defaultEngineKey;
  const costEngine = costEngineKey ? specsByKey.get(costEngineKey) ?? null : null;
  const costEnginePrice = latestSnapshot && costEngineKey ? computeAveragePrice(latestSnapshot, costEngineKey) : null;

  const generationFilters: GenerationFilter[] = generations.map(({ generation }) => ({
    generation,
    label: formatGenerationName(generation, model),
    years: generationYearRange(
      generationVariationKeys
        .filter((key) => key.generation === generation)
        .map((key) => specsByKey.get(`${generation}/${key.variation}`))
        .filter((spec): spec is EngineSpec => spec !== undefined),
    ),
  }));

  function toggleGeneration(generation: string) {
    const next = selectedGenerations.includes(generation)
      ? selectedGenerations.filter((g) => g !== generation)
      : [...selectedGenerations, generation];
    setSelectedGenerations(next);
    // A picked engine from a generation that's no longer selected would contradict the filter.
    if (activeGenerationKey && next.length > 0 && !next.includes(activeGenerationKey.split('/')[0])) {
      setActiveGenerationKey(null);
    }
  }

  // Everything priced on the page follows the picked engine, else the picked generations, else the whole model.
  const averagePrice = latestSnapshot ? computeAveragePrice(latestSnapshot, activeGenerationKey, selectedGenerations) : null;
  const trend = computePriceTrend(recentSnapshots(snapshots, TREND_WINDOW_DAYS), activeGenerationKey, selectedGenerations);
  const shownGenerations =
    selectedGenerations.length > 0 ? generations.filter(({ generation }) => selectedGenerations.includes(generation)) : generations;
  const adCount = shownGenerations.reduce((sum, generation) => sum + generation.adCount, 0);

  const breadcrumbs = (
    <Breadcrumbs sx={{ mb: 2 }}>
      <Link to="/cars">All brands</Link>
      <Link to={`/cars/${encodeURIComponent(make)}`}>{make}</Link>
      <Typography color="text.primary">{model}</Typography>
    </Breadcrumbs>
  );

  if (isLoading) {
    return (
      <PageContainer>
        {breadcrumbs}
        <Skeleton variant="text" width={280} height={48} />
        <Skeleton variant="rounded" height={96} sx={{ my: 3 }} />
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </PageContainer>
    );
  }

  if (isError || snapshots.length === 0) {
    return (
      <PageContainer>
        {breadcrumbs}
        <Typography variant="h4" gutterBottom>
          {make} {model}
        </Typography>
        <Alert severity="warning" sx={{ mb: 3 }}>
          We couldn&apos;t find any price history for {make} {model}.
        </Alert>
        {/* Live ads don't depend on our price history, so still show them (without deal badges). */}
        <LiveListings make={make} model={model} yearPrices={null} fallbackImageUrl={fallbackImageUrl} />
      </PageContainer>
    );
  }

  const TrendIcon = trend ? TREND_STYLE[trend.direction].icon : null;
  const selectedLabel = shownGenerations.map(({ generation }) => formatGenerationName(generation, model)).join(', ');
  const activeLabel = activeGenerationKey
    ? activeEngine?.engine ?? activeGenerationKey.replace('/', ' · ')
    : selectedGenerations.length > 0
      ? `all ${selectedLabel} engines`
      : 'all engines';

  const sidebar = (
    <Stack spacing={3}>
      <GenerationGuide
        model={model}
        generations={generations}
        selected={selectedGenerations}
        onToggle={toggleGeneration}
      />
      <CarReliabilityCard make={make} model={model} />
    </Stack>
  );

  return (
    <PageContainer>
      {breadcrumbs}
      <Typography variant="h4" gutterBottom>
        {make} {model}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        What it costs on the Bulgarian market, the ads worth a look right now, and what it costs to own.
      </Typography>

      <GenerationPicker
        filters={generationFilters}
        selected={selectedGenerations}
        onToggle={toggleGeneration}
        onClear={() => setSelectedGenerations([])}
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 2, mb: 4 }}>
        <StatCard
          label="Typical asking price"
          value={averagePrice !== null ? formatEur(averagePrice) : '—'}
          caption={activeGenerationKey ? activeLabel : selectedGenerations.length > 0 ? selectedLabel : 'Across all engines and years'}
        />
        <StatCard
          label="Price change, last 3 months"
          value={trend ? `${trend.changePercent > 0 ? '+' : ''}${trend.changePercent.toFixed(1)}%` : '—'}
          valueColor={trend ? TREND_STYLE[trend.direction].color : undefined}
          icon={TrendIcon ? <TrendIcon sx={{ color: TREND_STYLE[trend!.direction].color }} /> : undefined}
          caption={trend ? (trend.direction === 'down' ? 'Getting cheaper' : trend.direction === 'up' ? 'Getting pricier' : 'Holding steady') : undefined}
        />
        <Box sx={{ gridColumn: { xs: '1 / -1', md: 'auto' }, display: 'grid' }}>
          <StatCard
            label="Ads on mobile.bg last week"
            value={adCount > 0 ? adCount.toLocaleString() : '—'}
            caption={
              selectedGenerations.length > 0
                ? selectedLabel
                : generations.length > 1
                  ? `${generations.length} generations to choose from`
                  : undefined
            }
          />
        </Box>
      </Box>

      <Card sx={{ mb: 4 }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ sm: 'center' }}
            spacing={2}
            sx={{ mb: 2 }}
          >
            <Box>
              <Typography variant="h6">Price history</Typography>
              <Typography variant="body2" color="text.secondary">
                Average asking price for {activeLabel}, from our weekly market snapshots.
              </Typography>
            </Box>
            <TextField
              select
              size="small"
              label="Engine"
              value={activeGenerationKey ?? ''}
              onChange={(event) => setActiveGenerationKey(event.target.value || null)}
              sx={{ minWidth: { sm: 260 } }}
              slotProps={{ select: { displayEmpty: true, renderValue: () => (activeGenerationKey ? activeLabel : 'All engines'), MenuProps: { slotProps: { paper: { sx: { maxHeight: 420 } } } } }, inputLabel: { shrink: true } }}
            >
              <MenuItem value="">All engines</MenuItem>
              {shownGenerations.flatMap(({ generation }) => [
                <ListSubheader key={`header-${generation}`}>{formatGenerationName(generation, model)}</ListSubheader>,
                ...generationVariationKeys
                  .filter((key) => key.generation === generation)
                  .map(({ variation }) => {
                    const key = `${generation}/${variation}`;
                    const price = computeAveragePrice(latestSnapshot, key);
                    return (
                      <MenuItem key={key} value={key} sx={{ justifyContent: 'space-between', gap: 2 }}>
                        <span>{specsByKey.get(key)?.engine ?? variation.replace(/-/g, ' ')}</span>
                        {price !== null && (
                          <Typography component="span" variant="body2" color="text.secondary">
                            {formatEur(price)}
                          </Typography>
                        )}
                      </MenuItem>
                    );
                  }),
              ])}
            </TextField>
          </Stack>

          <Chart data={snapshots} generation={activeGenerationKey} generations={selectedGenerations} countries={selectedCountries} />
          {availableCountries.length > 0 && (
            <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', rowGap: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center', mr: 1 }}>
                Compare with prices in:
              </Typography>
              {availableCountries.map((country) => (
                <Chip
                  key={country}
                  size="small"
                  label={country}
                  color={selectedCountries.includes(country) ? 'secondary' : 'default'}
                  onClick={() => toggleCountry(country)}
                />
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>

      {LISTINGS_API_URL ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 2fr) minmax(0, 1fr)' },
            gap: 3,
            alignItems: 'start',
            mb: 4,
          }}
        >
          <LiveListings
            make={make}
            model={model}
            yearPrices={getYearPriceStats(latestSnapshot)}
            fallbackImageUrl={fallbackImageUrl}
            generationFilters={generationFilters}
            selectedGenerations={selectedGenerations}
          />
          {sidebar}
        </Box>
      ) : (
        <Box sx={{ mb: 4 }}>{sidebar}</Box>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
        <Card sx={{ minWidth: 0, p: { xs: 2, sm: 3 } }}>
          <ExpensesCalculator
            engine={costEngine}
            currentPrice={costEnginePrice}
            engineNote={
              activeGenerationKey
                ? undefined
                : `the most common ${selectedGenerations.length > 0 ? `${selectedLabel} ` : ''}engine, pick another in the Engine menu`
            }
          />
        </Card>

        <Card sx={{ minWidth: 0, p: { xs: 2, sm: 3 } }}>
          <Typography variant="h6">Specs</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {costEngine ? costEngine.engine : 'Pick an engine in the price history to see its spec sheet.'}
          </Typography>
          {!costEngineKey ? null : !costEngine ? (
            <CircularProgress size={20} />
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 2.5 }}>
              {SPEC_FIELDS.map((field) => (
                <Box key={field.label}>
                  <Typography variant="caption" color="text.secondary">
                    {field.label}
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {field.get(costEngine)}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Card>
      </Box>
    </PageContainer>
  );
}
